import React from "react";

import { createDialogEmitter } from "#app/lib/dialog-emitter";

import { CustomFieldsManageDialog } from "#app/components/custom-fields/custom-fields-manage-dialog";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { CustomFieldType } from "#app/lib/data";

// An optional "open straight into a screen" intent. `editField` opens the
// manager with that field's edit panel already up — a table column header's
// or a view's Fields page's "Edit field" action, which holds the field's
// definition and shouldn't make the user find it in the manager list again.
// `createField` opens the create form directly — on `type` when the picker
// already asked which kind, else on Text with the switcher in the form's
// title — and hands the created field to `onCreated`, so a picker that
// offered "Create Field" can attach it where the user was — the manager
// closes on that create instead of dropping the user in its list. `seed`
// starts that form from a definition the caller already holds (a form
// builder question's label and choices).
export type CustomFieldsDialogIntent =
  | { editField: CustomFieldDef }
  | {
      createField: true;
      type?: CustomFieldType;
      seed?: CustomFieldDef | undefined;
      onCreated?: (field: CustomFieldDef) => void;
    };

type CustomFieldsDialogActions = {
  openDialog: (intent?: CustomFieldsDialogIntent) => void;
  closeDialog: () => void;
};

const customFieldsDialog = createDialogEmitter<CustomFieldsDialogIntent | null>("custom_fields");

/** Get stable open/close callbacks. Works inside bottom-sheet portals. */
export function useCustomFieldsDialogActions(): CustomFieldsDialogActions {
  return React.useMemo<CustomFieldsDialogActions>(
    () => ({
      openDialog: (intent) => customFieldsDialog.open(intent ?? null),
      closeDialog: customFieldsDialog.close,
    }),
    [],
  );
}

export function CustomFieldsDialog() {
  // The intent survives close so the closing animation does not flash the
  // bare list.
  const { open, payload: intent } = customFieldsDialog.useDialogState();
  return (
    <CustomFieldsManageDialog
      open={open}
      intent={intent}
      onClose={customFieldsDialog.close}
    />
  );
}
